/**
 * Resolve a pathname against `APP_ROUTES` — the app's one route table.
 *
 * Both panes resolve through here. The primary pane used to let `<Routes>` do
 * it; now that a tab is what renders, the same resolver answers for both, so
 * one pathname can only ever mean one page.
 */
import { matchRoutes } from 'react-router-dom';

import { APP_ROUTES } from './appRoutes';

import type { AppRoute } from './appRoutes';

/**
 * `APP_ROUTES` as react-router route objects, each carrying its table entry.
 * Built once: `matchRoutes` ranks by specificity, which does not depend on
 * this array's order, so it is safe to hoist.
 */
const MATCHABLE_ROUTES = APP_ROUTES.map(route => ({ path: route.path, entry: route }));

/** A resolved route and the params its dynamic segments captured. */
export interface ResolvedRoute {
  route: AppRoute;
  params: Record<string, string>;
}

/**
 * The table entry a pathname resolves to, or `null` when nothing matches.
 *
 * Uses `matchRoutes` — react-router's own resolver — rather than taking the
 * first `matchPath` hit in table order. The two agree today only because the
 * table is hand-ordered, held together by "must precede" comments next to the
 * parameterised entries: in table order `/study/:channelId` above
 * `/study/ideas` would swallow it. `matchRoutes` ranks by specificity, so a
 * static segment always beats a dynamic one wherever each sits in the array,
 * and a pane resolves exactly what the real router would.
 */
export function resolveRoute(pathname: string): ResolvedRoute | null {
  const matches = matchRoutes(MATCHABLE_ROUTES, pathname);
  const match = matches?.[0];
  if (!match) return null;
  return {
    route: match.route.entry,
    params: match.params as Record<string, string>,
  };
}
